import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const CLEANUP_UI = {
  orders: { label: 'Orders', scopes: [
    { key: 'archived', label: 'Archived only', tone: 'ghost' },
    { key: 'non_delivered', label: 'All non-delivered', tone: 'ghost' },
    { key: 'all', label: 'ALL orders', tone: 'danger' },
  ] },
  carts: { label: 'Cart items', scopes: [{ key: 'all', label: 'Clear all cart items', tone: 'danger' }] },
  favorites: { label: 'Saved designs (favorites)', scopes: [{ key: 'all', label: 'Clear all favorites', tone: 'danger' }] },
  notifications: { label: 'Notifications', scopes: [
    { key: 'read', label: 'Clear read', tone: 'ghost' },
    { key: 'all', label: 'ALL notifications', tone: 'danger' },
  ] },
  user_designs: { label: 'User-saved designs', scopes: [
    { key: 'drafts', label: 'Only drafts', tone: 'ghost' },
    { key: 'rejected', label: 'Rejected only', tone: 'ghost' },
    { key: 'all', label: 'ALL user designs', tone: 'danger' },
  ] },
  community: { label: 'Community listings', scopes: [
    { key: 'rejected', label: 'Rejected only', tone: 'ghost' },
    { key: 'approved', label: 'Approved only', tone: 'ghost' },
    { key: 'all_moderated', label: 'All submitted', tone: 'danger' },
  ] },
  reports: { label: 'Content reports', scopes: [
    { key: 'resolved', label: 'Resolved / dismissed', tone: 'ghost' },
    { key: 'all', label: 'ALL reports', tone: 'danger' },
  ] },
  messages: { label: 'Contact messages', scopes: [
    { key: 'replied', label: 'Replied only', tone: 'ghost' },
    { key: 'all', label: 'ALL messages', tone: 'danger' },
  ] },
  users: { label: 'Customer users (cascades their data)', scopes: [
    { key: 'unverified', label: 'Unverified only', tone: 'ghost' },
    { key: 'no_orders', label: 'Customers with no orders', tone: 'ghost' },
    { key: 'all_customers', label: 'ALL customers', tone: 'danger' },
  ] },
  analytics: { label: 'Analytics events', scopes: [
    { key: 'older_30d', label: 'Older than 30 days', tone: 'ghost' },
    { key: 'all', label: 'ALL analytics', tone: 'danger' },
  ] },
  designs_archive: { label: 'Archived stock designs', scopes: [
    { key: 'archived', label: 'Archived only', tone: 'ghost' },
  ] },
};

export const DataCleanupCard = () => {
  const [counts, setCounts] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const confirm = useConfirm();

  const load = () => api.get('/admin/cleanup/counts').then(({ data }) => setCounts(data));
  useEffect(() => { load(); }, []);

  const run = async (entity, scope, scopeLabel) => {
    const total = counts?.[entity]?.counts?.[scope];
    const ok = await confirm({
      title: `Delete ${CLEANUP_UI[entity].label.toLowerCase()}?`,
      description: `This permanently removes "${scopeLabel}" (${total ?? '?'} records). This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setBusyKey(`${entity}-${scope}`);
    try {
      const { data } = await api.post('/admin/cleanup', null, { params: { entity, scope } });
      toast.success(`Deleted ${data.deleted} record${data.deleted === 1 ? '' : 's'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally { setBusyKey(null); }
  };

  return (
    <Card className="jw-card p-5 space-y-4" data-testid="admin-settings-cleanup">
      <div className="flex items-center gap-2">
        <Trash2 className="w-4 h-4 text-orange-400" />
        <h3 className="font-semibold">Data cleanup</h3>
      </div>
      <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
        Wipe test data before going live. Deleting <b>users</b> also cascades their carts, favorites, notifications, designs, orders, likes and analytics events.
      </p>

      <div className="grid gap-3">
        {Object.entries(CLEANUP_UI).map(([entity, cfg]) => (
          <div key={entity} className="rounded-lg border p-3" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-bg-elev)' }} data-testid={`admin-cleanup-row-${entity}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div>
                <p className="font-medium text-sm">{cfg.label}</p>
                <p className="text-[11px]" style={{ color: 'var(--jw-text-subtle)' }}>
                  {counts?.[entity]?.counts?._total ?? '…'} total
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {cfg.scopes.map((s) => {
                const c = counts?.[entity]?.counts?.[s.key] ?? 0;
                const busy = busyKey === `${entity}-${s.key}`;
                return (
                  <Button
                    key={s.key}
                    size="sm"
                    onClick={() => run(entity, s.key, s.label)}
                    disabled={busy || c === 0}
                    data-testid={`admin-cleanup-${entity}-${s.key}-button`}
                    className={`h-8 ${s.tone === 'danger' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'jw-btn-gloss-ghost'}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    {s.label} <span className="ml-1 text-[10px] opacity-80">({c})</span>
                  </Button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default DataCleanupCard;
